import {createContext, useContext, useEffect, useMemo, useState} from "react";
import {useConfigCatClient, User} from "configcat-react";
import {AuthContext} from "./AuthProvider";
import _ from "lodash";
import {Constants} from "../constants/constants";
import log from "loglevel";

type FeatureFlagProviderProps = {
  children: JSX.Element[] | JSX.Element;
};

type FeatureFlagState = {
  featureFlagsInitialized: boolean;
  manifestOptimization: boolean;
  driverRequirements: boolean;
  driverMessaging: boolean;
  colorSort: boolean;
  backendColorRules: boolean;
  datadogEnabled: boolean;
  datadogSessionSampleRate: number;
  datadogSessionReplaySampleRate: number;
  sentryEnabled: boolean;
  oktaDevMode: boolean;
  oktaMaxClockSkew: number;
  manifestPageSizes: number[];
  etas: boolean;
  globalFind: boolean;
  computedColumns: boolean;
  idexxLogicCompleteStops: boolean;
  askManifestCheckoutInfo: boolean;
  skills: boolean;
  tempPassword: boolean;
  multipleSort: boolean;
  dispatchNote: boolean;
  etaNotifications: boolean;
  etaFromOptimization: boolean;
  routePlanning: boolean;
  splitJob: boolean;
  lessStopDataPoints: boolean;
  routeOptimizationBatchModeStops: number;
  routePlanningWithBothStops: boolean;
  closedNeverReassign: boolean;
  routePlanningMap: boolean;
  etaNotificationPremium: boolean;
  itineraryPlanningSkills: boolean;
  etaWaitForEarlyTime: boolean;
  suggestedAssignment: boolean;
  existingStopsIP: boolean;
  constraintsIP: boolean;
  csvImportIP: boolean;
};

export const initialState: FeatureFlagState = {
  featureFlagsInitialized: false,
  manifestOptimization: false,
  driverRequirements: false,
  driverMessaging: false,
  colorSort: false,
  backendColorRules: false,
  datadogEnabled: true,
  datadogSessionSampleRate: 100,
  datadogSessionReplaySampleRate: 100,
  sentryEnabled: false,
  oktaDevMode: false,
  oktaMaxClockSkew: Constants.DEFAULT_MAX_CLOCK_SKEW_SECONDS,
  manifestPageSizes: Constants.MANIFEST_PAGE_SIZE_OPTIONS,
  etas: false,
  globalFind: false,
  computedColumns: false,
  idexxLogicCompleteStops: false,
  askManifestCheckoutInfo: false,
  skills: false,
  tempPassword: false,
  multipleSort: false,
  dispatchNote: false,
  etaNotifications: false,
  etaFromOptimization: false,
  routePlanning: false,
  splitJob: false,
  lessStopDataPoints: false,
  routeOptimizationBatchModeStops: Constants.BATCH_OPTIMIZATION_NUMBER_OF_STOPS,
  routePlanningWithBothStops: false,
  closedNeverReassign: false,
  routePlanningMap: false,
  etaNotificationPremium: false,
  itineraryPlanningSkills: false,
  etaWaitForEarlyTime: false,
  suggestedAssignment: false,
  existingStopsIP: false,
  constraintsIP: false,
  csvImportIP: false
};

const CSVNumberFlagKeys: (keyof FeatureFlagState)[] = ["manifestPageSizes"];

const FeatureFlagContext = createContext<FeatureFlagState>(initialState);

const FeatureFlagProvider = ({children}: FeatureFlagProviderProps) => {
  const client = useConfigCatClient();
  const [state, setState] = useState<FeatureFlagState>({
    ...initialState
  });

  const {user, tenant, tenantId} = useContext(AuthContext);
  const configCatUser = useMemo(() => {
    if (user && tenant && tenantId) {
      return {
        identifier: user.name,
        email: user.email,
        custom: {
          tenantId: tenantId,
          tenantName: tenant.shortName
        }
      } as User;
    }
  }, [user, tenant, tenantId]);

  const validKeys = Object.keys(initialState);

  useEffect(() => {
    if (configCatUser) {
      client.getAllValuesAsync(configCatUser).then((settings) => {
        const typedState: FeatureFlagState = {} as FeatureFlagState;
        const anyState: any = {};
        const invalidKeys: string[] = [];

        settings.forEach((setting) => {
          if (setting.settingValue != null) {
            if (!validKeys.includes(setting.settingKey)) {
              invalidKeys.push(setting.settingKey);
            } else if (CSVNumberFlagKeys.includes(setting.settingKey as any)) {
              anyState[setting.settingKey] = setting.settingValue.toString().split(",").map(Number);
            } else {
              anyState[setting.settingKey] = setting.settingValue;
            }
          }
        });
        if (invalidKeys.length > 0) {
          log.warn("Unmapped feature flags: %s", invalidKeys);
        }
        const newState = {...state, ...anyState, ...typedState} as FeatureFlagState;
        newState.featureFlagsInitialized = true;

        if (!_.isEqual(newState, state)) {
          setState(newState);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, configCatUser, validKeys]);

  return <FeatureFlagContext.Provider value={state}>{children}</FeatureFlagContext.Provider>;
};

const useFeatureFlagContext = () => {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error("useFeatureFlagContext must be used within a FeatureFlagProvider");
  }
  return context;
};

export default FeatureFlagProvider;
export {FeatureFlagContext, useFeatureFlagContext};
export type {FeatureFlagState};
